import { FormOrderBy, PacketOrderBy } from '@weave/schema-gen-ts/dist/schemas/forms-digital/forms_messages.pb';
import { PageRequest } from '@weave/schema-gen-ts/dist/schemas/forms-digital/weave_digital_forms.pb';
import { useInfiniteQuery, useQuery } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { LimitedSchemaInfiniteQueryOptions, LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaDigitalForms } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { requestKeys } from '../keys';

export const FORM_ORDER_TO_PACKET_ORDER: Record<FormOrderBy, PacketOrderBy> = {
  [FormOrderBy.FORM_ORDER_UNKNOWN]: PacketOrderBy.PACKET_ORDER_UNKNOWN,
  [FormOrderBy.FORM_ORDER_CREATED_AT]: PacketOrderBy.PACKET_ORDER_CREATED_AT,
  [FormOrderBy.FORM_ORDER_FREQUENTLY_USED]: PacketOrderBy.PACKET_ORDER_FREQUENTLY_USED,
  [FormOrderBy.FORM_ORDER_UPDATED_AT]: PacketOrderBy.PACKET_ORDER_UPDATED_AT,
  [FormOrderBy.FORM_ORDER_ALPHABETICAL]: PacketOrderBy.PACKET_ORDER_ALPHABETICAL,
};

/**
 * @param companyId - AKA locationId
 */
export const usePacketList = <E = unknown, T = IO.GetPacketList['output']>(
  req: Compulsory<IO.GetPacketList['input'], 'companyId'>,
  options?: LimitedSchemaQueryOptions<IO.GetPacketList, E, T>
) => {
  const queryKey = requestKeys.packetList(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetPacketList(req, { headers: { 'Location-Id': req.companyId } }),
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

/**
 * @param companyIds - AKA locationIds
 * @deprecated Use `usePacketListV3` instead
 */
export const usePacketListV2 = <E = unknown, T = IO.GetPacketListV2['output']>(
  req: Compulsory<IO.GetPacketListV2['input'], 'companyIds' | 'pageRequest'>,
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetPacketListV2, E, T>
) => {
  const queryKey = requestKeys.packetListV2(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: PageRequest }) =>
      SchemaDigitalForms.GetPackets({
        ...req,
        pageRequest: pageParam || req.pageRequest,
      }),
    getNextPageParam: (lastPage): PageRequest => {
      const page = lastPage.pageMetadata?.page;
      return {
        pageNumber: page ? parseInt(page, 10) : undefined,
        pageLimit: req.pageRequest.pageLimit,
      };
    },
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

type GetPacketsListV3Request = Compulsory<IO.GetPacketListV3['input'], 'orgRequest' | 'pageRequest'>;
type ModifiedPacketsListV3Response = Omit<IO.GetPacketListV3['output'], 'data'> & {
  rows: IO.GetPacketListV3['output']['data'];
};

/**
 * Fetches the list of packets in the organization and returns the response
 * in a format that is compatible with the InfinitePaginatedList component.
 */
async function getPacketsListV3(req: GetPacketsListV3Request): Promise<ModifiedPacketsListV3Response> {
  const response = await SchemaDigitalForms.GetPacketsV3(req);
  return {
    pageMetadata: response.pageMetadata,
    rows: response.data ?? [], // This is required for the InfinitePaginatedList component to work.
  };
}

interface GetFormListV3ModifiedIO {
  input: GetPacketsListV3Request;
  output: ModifiedPacketsListV3Response;
}

/**
 * Fetches the list of packets in the organization.
 */
export const usePacketListV3 = <E = unknown, T = GetFormListV3ModifiedIO['output']>(
  req: GetPacketsListV3Request,
  options?: LimitedSchemaInfiniteQueryOptions<GetFormListV3ModifiedIO, E, T>
) => {
  const queryKey = requestKeys.packetListV3(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: PageRequest }) =>
      getPacketsListV3({ ...req, pageRequest: pageParam || req.pageRequest }),
    getNextPageParam: (lastPage): PageRequest | undefined => {
      const page = parseInt(lastPage.pageMetadata?.page || '0', 10);
      const lastPageNumber = parseInt(lastPage.pageMetadata?.lastPage || '0', 10);

      if (page >= lastPageNumber) {
        return undefined;
      }

      return {
        pageNumber: page + 1,
        pageLimit: req.pageRequest.pageLimit,
      };
    },
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetPacketDetails = <E = unknown, T = IO.GetPacketDetails['output']>(
  req: Compulsory<IO.GetPacketDetails['input'], 'packetId' | 'orgId'>,
  options?: LimitedSchemaQueryOptions<IO.GetPacketDetails, E, T>
) => {
  const queryKey = requestKeys.packetDetails(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetPacketDetails(req),
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export const useGetPacketPDF = <E = unknown, T = IO.GetFormPDFAuthed['output']>(
  req: Compulsory<IO.GetFormPDFAuthed['input'], 'packetId' | 'companyId'>,
  options?: LimitedSchemaQueryOptions<IO.GetFormPDFAuthed, E, T>
) => {
  const queryKey = requestKeys.packetPDF(req);
  const query = useQuery({
    queryKey,
    queryFn: () => SchemaDigitalForms.GetFormPDFAuthed(req),
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};
